/**
 * Utilidades para mejorar la accesibilidad en la aplicación
 */

/**
 * Genera un ID único para un elemento de la interfaz
 * @param prefix - Prefijo para el ID
 * @returns ID único
 */
export const generateId = (prefix: string): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Determina si se debe anunciar un cambio a los lectores de pantalla
 * @param message - Mensaje a anunciar
 * @param priority - Prioridad del anuncio (polite, assertive)
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite'): void => {
  // Buscar un elemento de anuncio existente o crear uno nuevo
  let announcer = document.getElementById(`sr-announcer-${priority}`);

  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = `sr-announcer-${priority}`;
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.position = 'absolute';
    announcer.style.width = '1px';
    announcer.style.height = '1px';
    announcer.style.margin = '-1px';
    announcer.style.padding = '0';
    announcer.style.overflow = 'hidden';
    announcer.style.clip = 'rect(0, 0, 0, 0)';
    announcer.style.whiteSpace = 'nowrap';
    announcer.style.border = '0';
    document.body.appendChild(announcer);
  }

  // Limpiar el anunciador y luego establecer el mensaje
  announcer.textContent = '';

  // Usar setTimeout para asegurarse de que el cambio sea detectado por los lectores de pantalla
  setTimeout(() => {
    announcer!.textContent = message;
  }, 50);
};

/**
 * Hook para manejar el foco en elementos cuando se abren modales o diálogos
 * @param elementRef - Referencia al elemento que debe recibir el foco
 * @param isOpen - Si el elemento está visible/abierto
 * @param returnFocusRef - Referencia al elemento que debe recibir el foco cuando se cierra
 * @returns Función para limpiar el efecto (devolver el foco)
 */
export const manageFocus = (
  elementRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
  returnFocusRef?: React.RefObject<HTMLElement>
): (() => void) | undefined => {
  if (isOpen && elementRef.current) {
    // Guardar el elemento activo actual
    const activeElement = document.activeElement as HTMLElement;

    // Enfocar el nuevo elemento
    elementRef.current.focus();

    // Cuando se cierra, devolver el foco al elemento anterior
    return () => {
      if (returnFocusRef && returnFocusRef.current) {
        returnFocusRef.current.focus();
      } else if (activeElement) {
        activeElement.focus();
      }
    };
  }
  return undefined;
};

/**
 * Crea un manejador de teclado para elementos interactivos
 * @param onClick - Función a ejecutar cuando se activa el elemento
 * @returns Manejador de eventos de teclado
 */
export const handleKeyboardActivation = (onClick: () => void): ((event: React.KeyboardEvent) => void) => {
  return (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };
};

/**
 * Proporciona atributos ARIA comunes para componentes
 * @param id - ID base para los elementos
 * @param labelText - Texto para el label
 * @param descriptionText - Texto para la descripción (opcional)
 * @returns Objeto con atributos ARIA
 */
export const getAriaAttributes = (
  id: string,
  _labelText: string,
  descriptionText?: string
) => {
  const labelId = `${id}-label`;
  const descriptionId = descriptionText ? `${id}-description` : undefined;

  return {
    labelProps: {
      id: labelId,
    },
    descriptionProps: descriptionText
      ? {
        id: descriptionId,
      }
      : {},
    elementProps: {
      'aria-labelledby': labelId,
      'aria-describedby': descriptionId,
    },
  };
};
